import { AlertGroup, AlertRule, AlertSensor, AlertTemplate, AlertVariables } from '../types/Alerts'
import { AlertRuleDataGridRow } from '../components/AlertRuleDataGrid'
import { UserAlertRuleDataGridRow } from '../components/UserAlertRuleDialog'
import {
  PrometheusAlert,
  RuuviTagBattery,
  RuuviTagCondition,
  Sensor,
  UserAlertRule,
  UserAlertRuleInput,
  ValveSensor
} from '../types'
import { AlertRuleName, AlertPhase, AlertSeverity, AlertStatus, AlertGroupName } from '../enums'
import i18next from 'i18next'
import { AlertRuleField } from '../components/AlertRuleFieldSelect'
import {
  formatBatteryVoltage,
  formatCurrent,
  formatHumidity,
  formatPercentage,
  formatPressure,
  formatRssi,
  formatTemperature,
  formatTemperatureDelta,
  formatValue,
  formatVoltage
} from './string'

export function buildAlertStructure(
  data: AlertRuleDataGridRow[],
  alertTemplates: AlertTemplate[]
): AlertGroup[] {
  const alertRules: AlertGroup[] = alertTemplates.map((template): AlertGroup => {
    return {
      name: template.name,
      alerts: Object.entries(template.defaults).map(([alertName, alert]): AlertRule => {
        return {
          name: alertName as AlertRuleName,
          enabled: alert.enabled,
          variables: alert.variables
        }
      })
    }
  })

  data.forEach((row: AlertRuleDataGridRow) => {
    const alertGroup = alertRules.find((template) => template.name === row.group) as AlertGroup

    if (alertGroup === undefined) {
      return
    }

    const alertRule: AlertRule = {
      name: row.name,
      enabled: row.enabled,
      sensor: row?.sensor,
      severity: row?.severity,
      variables: row.variables
    }

    const alertIndex = alertGroup.alerts.findIndex((alert) => alert.name === row.name)
    if (alertIndex === -1) {
      alertGroup.alerts.push(alertRule)
    } else {
      alertGroup.alerts[alertIndex] = alertRule
    }
  })

  return alertRules
}

export function checkAlertValueUnit(
  alertname: string,
  projectAlertRules: AlertGroup[],
  variable?: keyof AlertVariables
) {
  let unit
  projectAlertRules.map((group: AlertGroup) => {
    group.alerts.map((alert: AlertRule) => {
      if (alert.name === alertname) {
        if (variable) {
          unit = alert.variables[variable]?.unit
        } else {
          unit = alert.variables.param1.unit
        }
      }
    })
  })
  return unit
}

export function buildAlertStructureForUser(data: UserAlertRuleDataGridRow[]) {
  const alertRules: UserAlertRuleInput[] = []

  data.map((rule: UserAlertRuleDataGridRow) => {
    if (rule?.enabled || rule.appNotificationEnabled || rule.emailNotificationEnabled)
      alertRules.push({
        ruleGroupName: rule.ruleGroupName,
        alertRuleName: rule.alertRuleName,
        emailNotificationEnabled: rule?.enabled ?? rule.emailNotificationEnabled,
        smsNotificationEnabled: rule?.enabled ?? rule.smsNotificationEnabled,
        appNotificationEnabled: rule?.enabled ?? rule.appNotificationEnabled
      })
  })

  return alertRules
}

/**
 * @deprecated
 */
export function isDeviceAlerting(
  sensor: Sensor | null,
  alertRules: AlertGroup[]
): AlertRuleName | null {
  if (!sensor || !alertRules?.length) {
    return null
  }

  for (const group of alertRules) {
    for (const alert of group.alerts) {
      if (alert.enabled && compareValues(sensor, alert)) {
        return alert.name as AlertRuleName
      }
    }
  }

  return null
}

/**
 * @deprecated
 */
function compareValues(sensor: Sensor, alert: AlertRule): boolean {
  if (!('param1' in alert.variables) || typeof alert.variables.param1.value !== 'number') {
    return false
  }

  switch (alert.name) {
    case AlertRuleName.SHORT_CIRCUIT:
    case AlertRuleName.HIGH_CURRENT:
    case AlertRuleName.OVERLOADED_CURRENT:
    case AlertRuleName.LOW_CURRENT:
      return (
        (sensor.current_1 !== null &&
          (sensor.current_1 / sensor.rated_current) * 100 > alert.variables.param1.value) ||
        (sensor.current_2 !== null &&
          (sensor.current_2 / sensor.rated_current) * 100 > alert.variables.param1.value) ||
        (sensor.current_3 !== null &&
          (sensor.current_3 / sensor.rated_current) * 100 > alert.variables.param1.value)
      )

    case AlertRuleName.HIGH_HUMIDITY:
      return sensor.rh > alert.variables.param1.value
    case AlertRuleName.LOW_SIGNAL:
      return sensor.rssi < alert.variables.param1.value
    case AlertRuleName.HIGH_TEMPERATURE:
      return sensor.temp > alert.variables.param1.value
    case AlertRuleName.HIGH_VOLTAGE:
      return (
        (sensor.voltage_1 !== null && sensor.voltage_1 > alert.variables.param1.value) ||
        (sensor.voltage_2 !== null && sensor.voltage_2 > alert.variables.param1.value) ||
        (sensor.voltage_3 !== null && sensor.voltage_3 > alert.variables.param1.value)
      )
    case AlertRuleName.LOW_VOLTAGE:
      return (
        (sensor.voltage_1 !== null && sensor.voltage_1 < alert.variables.param1.value) ||
        (sensor.voltage_2 !== null && sensor.voltage_2 < alert.variables.param1.value) ||
        (sensor.voltage_3 !== null && sensor.voltage_3 < alert.variables.param1.value)
      )
    default:
      return false
  }
}

/**
 * Translates the alert rule name.
 */
export function translateAlertRuleName(name: AlertRuleName) {
  return i18next.t(`AlertRuleName.${name}`, { ns: 'alert' })
}

/**
 * Translates the alert rule group name.
 */
export function translateAlertGroupName(group: AlertGroupName) {
  return i18next.t(`AlertGroupName.${group}`, { ns: 'alert' })
}

/**
 * Translate an alert severity.
 */
export function translateAlertSeverity(severity: AlertSeverity) {
  return i18next.t(`AlertSeverity.${severity}`, { ns: 'alert' })
}

/**
 * Translate the alert status.
 */
export function translateAlertStatus(status: AlertStatus) {
  return i18next.t(`AlertStatus.${status}`, { ns: 'alert' })
}

/**
 * Translate the alert phase.
 */
export function translateAlertPhase(alert: PrometheusAlert) {
  if (!alert.labels.name) {
    return '-'
  }

  // Check if we need for translate the phase by "phase" label.
  // This is because single-phase devices always trigger for c1 or v1,
  // but the reading could be for L1, L2 or L3.
  switch (alert.labels?.name) {
    case AlertPhase.C1:
    case AlertPhase.C2:
    case AlertPhase.C3:
      return translateAlertCurrentPhase(alert)
    case AlertPhase.V1:
    case AlertPhase.V2:
    case AlertPhase.V3:
      return translateAlertVoltagePhase(alert)
  }

  return i18next.t(`AlertPhase.${alert.labels.name}`, { ns: 'alert' })
}

function translateAlertCurrentPhase(alert: PrometheusAlert) {
  switch (alert.labels?.phase) {
    case '1':
      return i18next.t(`AlertPhase.${AlertPhase.C1}`, { ns: 'alert' })
    case '2':
      return i18next.t(`AlertPhase.${AlertPhase.C2}`, { ns: 'alert' })
    case '3':
      return i18next.t(`AlertPhase.${AlertPhase.C3}`, { ns: 'alert' })
    default:
      return i18next.t(`AlertPhase.${alert.labels.name}`, { ns: 'alert' })
  }
}

function translateAlertVoltagePhase(alert: PrometheusAlert) {
  switch (alert.labels?.phase) {
    case '1':
      return i18next.t(`AlertPhase.${AlertPhase.V1}`, { ns: 'alert' })
    case '2':
      return i18next.t(`AlertPhase.${AlertPhase.V2}`, { ns: 'alert' })
    case '3':
      return i18next.t(`AlertPhase.${AlertPhase.V3}`, { ns: 'alert' })
    default:
      return i18next.t(`AlertPhase.${alert.labels.name}`, { ns: 'alert' })
  }
}

/**
 * Get the alert name.
 */
export function getAlertRuleName(alert: PrometheusAlert) {
  return alert?.labels?.alertname || AlertRuleName.UNKNOWN
}

/**
 * Get the alert severity.
 */
export function getAlertSeverity(alert: PrometheusAlert) {
  return alert?.labels?.severity || AlertSeverity.UNKNOWN
}

/**
 * Get the alert status.
 */
export function getAlertStatus(alert: PrometheusAlert) {
  return alert?.state || alert?.status || AlertStatus.UNKNOWN
}

/**
 * Get the unit of the alert value.
 */
export function getAlertValueUnit(alert: PrometheusAlert) {
  switch (alert.labels.alertname) {
    case AlertRuleName.HIGH_CURRENT:
    case AlertRuleName.OVERLOADED_CURRENT:
    case AlertRuleName.SHORT_CIRCUIT:
    case AlertRuleName.LOW_CURRENT:
      return '%'

    case AlertRuleName.HIGH_VOLTAGE:
    case AlertRuleName.LOW_VOLTAGE:
    case AlertRuleName.NO_VOLTAGE:
      return 'V'

    case AlertRuleName.LOW_SIGNAL:
    case AlertRuleName.WEAK_SIGNAL:
      return 'dBm'

    case AlertRuleName.HIGH_TEMPERATURE:
    case AlertRuleName.LOW_TEMPERATURE:
      return '°C'

    case AlertRuleName.HIGH_PRESSURE:
    case AlertRuleName.LOW_PRESSURE:
      return 'hPa'

    case AlertRuleName.HIGH_HUMIDITY:
    case AlertRuleName.LOW_HUMIDITY:
      return '%'

    case AlertRuleName.DEW_POINT:
      return '°C'

    case AlertRuleName.LOW_BATTERY:
      return 'mV'

    case AlertRuleName.CONDENSATION_RISK_INTAKE:
    case AlertRuleName.CONDENSATION_RISK_EXHAUST:
      return '°C'

    case AlertRuleName.RAPID_TEMPERATURE_DROP:
    case AlertRuleName.RAPID_TEMPERATURE_RISE:
      return '°C'

    case AlertRuleName.EXHAUST_AIR_TOO_COLD:
    case AlertRuleName.EXHAUST_AIR_TOO_HOT:
      return '°C'

    case AlertRuleName.DEVICE_OFFLINE:
    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
    case AlertRuleName.NO_HEATING:
    case AlertRuleName.TARGET_TEMPERATURE_NOT_REACHED:
    default:
      return alert.labels.unit
  }
}

export function getAlertValue(alert: PrometheusAlert) {
  return alert?.value !== undefined
    ? alert.value
    : alert.annotations?.value
      ? parseFloat(alert.annotations.value)
      : null
}

export function formatAlertValue(alert: PrometheusAlert, unit?: boolean, decimals?: number) {
  const value = getAlertValue(alert)

  if (value === null) {
    return '-'
  }

  switch (alert.labels.alertname) {
    case AlertRuleName.SHORT_CIRCUIT:
    case AlertRuleName.OVERLOADED_CURRENT:
    case AlertRuleName.HIGH_CURRENT:
    case AlertRuleName.LOW_CURRENT:
      // The value is percentage of rated current.
      return formatPercentage(value * 100, unit, decimals)

    case AlertRuleName.HIGH_VOLTAGE:
    case AlertRuleName.LOW_VOLTAGE:
    case AlertRuleName.NO_VOLTAGE:
      return formatVoltage(value, unit, decimals)

    case AlertRuleName.HIGH_TEMPERATURE:
    case AlertRuleName.LOW_TEMPERATURE:
      return formatTemperature(value, unit, decimals)

    case AlertRuleName.HIGH_HUMIDITY:
    case AlertRuleName.LOW_HUMIDITY:
      return formatHumidity(value, unit, decimals)

    case AlertRuleName.HIGH_PRESSURE:
    case AlertRuleName.LOW_PRESSURE:
      return formatPressure(value, unit, decimals)

    case AlertRuleName.DEW_POINT:
      return formatTemperature(value, unit, decimals)

    case AlertRuleName.WEAK_SIGNAL:
    case AlertRuleName.LOW_SIGNAL:
      return formatRssi(value, unit, decimals)

    case AlertRuleName.LOW_BATTERY:
      return formatBatteryVoltage(value, unit, decimals ?? 0)

    case AlertRuleName.DEVICE_OFFLINE:
      return '-'

    case AlertRuleName.CONDENSATION_RISK_INTAKE:
    case AlertRuleName.CONDENSATION_RISK_EXHAUST:
      return formatTemperatureDelta(value, unit, decimals)

    case AlertRuleName.EXHAUST_AIR_TOO_COLD:
      return formatTemperatureDelta(value, unit, decimals)

    case AlertRuleName.EXHAUST_AIR_TOO_HOT:
      return formatTemperature(value, unit, decimals)

    case AlertRuleName.RAPID_TEMPERATURE_DROP:
    case AlertRuleName.RAPID_TEMPERATURE_RISE:
      return formatTemperatureDelta(value, unit, decimals)

    case AlertRuleName.NO_HEATING:
    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
      return formatTemperatureDelta(value, unit, decimals)
  }

  const alertValueUnit = getAlertValueUnit(alert)
  if (alertValueUnit === '%') {
    return formatPercentage(value, unit)
  } else if (alertValueUnit === 'V') {
    return formatVoltage(value, unit)
  } else if (alertValueUnit === 'mV') {
    return formatBatteryVoltage(value, unit)
  } else if (alertValueUnit === 'A') {
    return formatCurrent(value, unit)
  } else if (alertValueUnit === 'dBm') {
    return formatRssi(value, unit)
  } else if (alertValueUnit === '°C') {
    return formatTemperature(value, unit)
  } else if (alertValueUnit === 'hPa') {
    return formatPressure(value, unit)
  }

  const formattedValue = formatValue(value, 2)
  return unit && alertValueUnit !== undefined
    ? `${formattedValue} ${alertValueUnit}`
    : formattedValue
}

export function getAlertThresholdValue(alert: PrometheusAlert) {
  return getAlertParam1Value(alert)
}

export function formatAlertThresholdValue(
  alert: PrometheusAlert,
  unit?: boolean,
  decimals?: number
) {
  return formatAlertParam1Value(alert, unit, decimals)
}

/**
 * Get the value of param1.
 */
export function getAlertParam1Value(alert: PrometheusAlert) {
  const param1 = alert?.labels?.param1
  return param1 ? parseFloat(param1) : null
}

/**
 * Format the value of param1.
 */
export function formatAlertParam1Value(alert: PrometheusAlert, unit?: boolean, decimals?: number) {
  const value = getAlertParam1Value(alert)

  if (value === null) {
    return '-'
  }

  switch (alert.labels.alertname) {
    case AlertRuleName.SHORT_CIRCUIT:
    case AlertRuleName.OVERLOADED_CURRENT:
    case AlertRuleName.HIGH_CURRENT:
    case AlertRuleName.LOW_CURRENT:
      return formatPercentage(value, unit, decimals ?? 0)

    case AlertRuleName.HIGH_VOLTAGE:
    case AlertRuleName.LOW_VOLTAGE:
    case AlertRuleName.NO_VOLTAGE:
      return formatVoltage(value, unit, decimals ?? 0)

    case AlertRuleName.HIGH_TEMPERATURE:
    case AlertRuleName.LOW_TEMPERATURE:
      return formatTemperature(value, unit, decimals ?? 0)

    case AlertRuleName.HIGH_HUMIDITY:
    case AlertRuleName.LOW_HUMIDITY:
      return formatHumidity(value, unit, decimals ?? 0)

    case AlertRuleName.HIGH_PRESSURE:
    case AlertRuleName.LOW_PRESSURE:
      return formatPressure(value, unit, decimals ?? 0)

    case AlertRuleName.DEW_POINT:
      return formatTemperature(value, unit, decimals ?? 0)

    case AlertRuleName.LOW_SIGNAL:
    case AlertRuleName.WEAK_SIGNAL:
      return formatRssi(value, unit, decimals ?? 0)

    case AlertRuleName.LOW_BATTERY:
      return formatBatteryVoltage(value, unit, decimals ?? 0)

    case AlertRuleName.DEVICE_OFFLINE:
      return alert.labels.sensor === AlertSensor.VALVE ? `${value} min` : '5 min'

    case AlertRuleName.RAPID_TEMPERATURE_DROP:
    case AlertRuleName.RAPID_TEMPERATURE_RISE:
      return formatTemperatureDelta(value, unit, decimals)

    case AlertRuleName.CONDENSATION_RISK_INTAKE:
    case AlertRuleName.CONDENSATION_RISK_EXHAUST:
      return formatTemperatureDelta(value, unit, decimals)

    case AlertRuleName.EXHAUST_AIR_TOO_COLD:
    case AlertRuleName.EXHAUST_AIR_TOO_HOT:
    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
    case AlertRuleName.NO_HEATING:
    case AlertRuleName.TARGET_TEMPERATURE_NOT_REACHED:
      return formatTemperature(value, unit, decimals ?? 0)
  }

  const alertValueUnit = getAlertValueUnit(alert)
  if (alertValueUnit === '%') {
    return formatPercentage(value, unit)
  } else if (alertValueUnit === 'V') {
    return formatVoltage(value, unit)
  } else if (alertValueUnit === 'mV') {
    return formatBatteryVoltage(value, unit)
  } else if (alertValueUnit === 'A') {
    return formatCurrent(value, unit)
  } else if (alertValueUnit === 'dBm') {
    return formatRssi(value, unit)
  } else if (alertValueUnit === '°C') {
    return formatTemperature(value, unit)
  } else if (alertValueUnit === 'hPa') {
    return formatPressure(value, unit)
  }

  const formattedValue = formatValue(value, 2)
  return unit && alertValueUnit !== undefined
    ? `${formattedValue} ${alertValueUnit}`
    : formattedValue
}

export function getAlertParam2Value(alert: PrometheusAlert) {
  const param2 = alert?.labels?.param2

  switch (alert.labels.alertname) {
    case AlertRuleName.NO_HEATING:
    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
    case AlertRuleName.TARGET_TEMPERATURE_NOT_REACHED:
      return param2 ? parseFloat(param2) : null
    default:
      return null
  }
}

export function formatAlertParam2Value(alert: PrometheusAlert, unit?: boolean, decimals?: number) {
  const value = getAlertParam2Value(alert)

  if (value === null) {
    return '-'
  }

  switch (alert.labels.alertname) {
    case AlertRuleName.NO_HEATING:
      // The value is an absolute value.
      return formatPercentage(value / 100, unit, decimals)

    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
      return formatPercentage(value, unit, decimals)

    case AlertRuleName.TARGET_TEMPERATURE_NOT_REACHED:
      return unit ? `${value} min` : `${value}`

    default:
      return '-'
  }
}

export function getAlertValueBySensor(alert: PrometheusAlert, sensor: Sensor) {
  switch (alert.labels.name) {
    case AlertPhase.C1:
    case AlertPhase.C2:
    case AlertPhase.C3:
      return getAlertCurrentValueBySensor(alert, sensor)
    case AlertPhase.V1:
    case AlertPhase.V2:
    case AlertPhase.V3:
      return getAlertVoltageValueBySensor(alert, sensor)
    case AlertPhase.TEMP:
      return sensor.avg_temp
    case AlertPhase.RH:
      return sensor.avg_rh
    case AlertPhase.RSSI:
      return sensor.avg_rssi
    default:
      return null
  }
}

function getAlertCurrentValueBySensor(alert: PrometheusAlert, sensor: Sensor) {
  // Check the value by "phase" label when present, because single-phase devices always trigger for c1.
  switch (alert.labels?.phase) {
    case '1':
      return sensor.avg_current_1 !== null
        ? (sensor.avg_current_1 / sensor.rated_current) * 100
        : null
    case '2':
      return sensor.avg_current_2 !== null
        ? (sensor.avg_current_2 / sensor.rated_current) * 100
        : null
    case '3':
      return sensor.avg_current_3 !== null
        ? (sensor.avg_current_3 / sensor.rated_current) * 100
        : null
  }

  switch (alert.labels.name) {
    case AlertPhase.C1:
      return sensor.avg_current_1 !== null
        ? (sensor.avg_current_1 / sensor.rated_current) * 100
        : null
    case AlertPhase.C2:
      return sensor.avg_current_2 !== null
        ? (sensor.avg_current_2 / sensor.rated_current) * 100
        : null
    case AlertPhase.C3:
      return sensor.avg_current_3 !== null
        ? (sensor.avg_current_3 / sensor.rated_current) * 100
        : null
  }

  return null
}

function getAlertVoltageValueBySensor(alert: PrometheusAlert, sensor: Sensor) {
  // Check the value by "phase" label when present, because single-phase devices always trigger for v1.
  switch (alert.labels?.phase) {
    case '1':
      return sensor.avg_voltage_1
    case '2':
      return sensor.avg_voltage_2
    case '3':
      return sensor.avg_voltage_3
  }

  switch (alert.labels.name) {
    case AlertPhase.V1:
      return sensor.avg_voltage_1
    case AlertPhase.V2:
      return sensor.avg_voltage_2
    case AlertPhase.V3:
      return sensor.avg_voltage_3
  }

  return null
}

/**
 * Get the alerting current value.
 */
export function getAlertingCurrentValueBySensor(alert: PrometheusAlert, sensor: Sensor) {
  if (
    alert.labels.alertname !== AlertRuleName.HIGH_CURRENT &&
    alert.labels.alertname !== AlertRuleName.LOW_CURRENT &&
    alert.labels.alertname !== AlertRuleName.OVERLOADED_CURRENT &&
    alert.labels.alertname !== AlertRuleName.SHORT_CIRCUIT
  ) {
    return null
  }

  switch (alert.labels.phase) {
    case '1':
      return sensor.avg_current_1
    case '2':
      return sensor.avg_current_2
    case '3':
      return sensor.avg_current_3
  }

  switch (alert.labels.name) {
    case AlertPhase.C1:
      return sensor.avg_current_1
    case AlertPhase.C2:
      return sensor.avg_current_2
    case AlertPhase.C3:
      return sensor.avg_current_3
    default:
      return null
  }
}

export function getAlertValueByValveSensor(alert: PrometheusAlert, sensor: ValveSensor) {
  switch (alert.labels.alertname) {
    case AlertRuleName.CONDENSATION_RISK_INTAKE:
      return sensor.inTemperature !== null && sensor.inDewPoint !== null
        ? sensor.inTemperature - sensor.inDewPoint
        : null

    case AlertRuleName.CONDENSATION_RISK_EXHAUST:
      return sensor.outTemperature !== null && sensor.outDewPoint !== null
        ? sensor.outTemperature - sensor.outDewPoint
        : null

    case AlertRuleName.EXHAUST_AIR_TOO_COLD:
      return sensor.setTemperature !== null && sensor.inTemperature !== null
        ? sensor.setTemperature - sensor.inTemperature
        : null

    case AlertRuleName.EXHAUST_AIR_TOO_HOT:
      return sensor.outTemperature !== null ? sensor.outTemperature : null

    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
    case AlertRuleName.NO_HEATING:
      return sensor.outTemperature !== null && sensor.inTemperature !== null
        ? sensor.outTemperature - sensor.inTemperature
        : null

    case AlertRuleName.TARGET_TEMPERATURE_NOT_REACHED:
      return sensor.setTemperature !== null && sensor.outTemperature !== null
        ? sensor.setTemperature - sensor.outTemperature
        : null

    case AlertRuleName.RAPID_TEMPERATURE_DROP:
    case AlertRuleName.RAPID_TEMPERATURE_RISE:
      return sensor.inTemperature !== null ? sensor.inTemperature : null

    case AlertRuleName.WEAK_SIGNAL:
      return sensor.rssi !== null ? sensor.rssi : null

    default:
      return null
  }
}

/**
 * Format the alerting by valve sensor value.
 */
export function formatAlertValueByValveSensor(
  alert: PrometheusAlert,
  sensor: ValveSensor,
  unit?: boolean,
  decimals?: number
) {
  const value = getAlertValueByValveSensor(alert, sensor)

  if (value === null) {
    return '-'
  }

  switch (alert.labels.alertname) {
    case AlertRuleName.CONDENSATION_RISK_INTAKE:
    case AlertRuleName.CONDENSATION_RISK_EXHAUST:
      return formatTemperatureDelta(value, unit, decimals)

    case AlertRuleName.EXHAUST_AIR_TOO_COLD:
      return formatTemperatureDelta(value, unit, decimals)

    case AlertRuleName.EXHAUST_AIR_TOO_HOT:
      return formatTemperature(value, unit, decimals)

    case AlertRuleName.RAPID_TEMPERATURE_RISE:
    case AlertRuleName.RAPID_TEMPERATURE_DROP:
      return formatTemperatureDelta(value, unit, decimals)

    case AlertRuleName.NO_HEATING:
    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
      return formatTemperatureDelta(value, unit, decimals)

    case AlertRuleName.TARGET_TEMPERATURE_NOT_REACHED:
      return formatTemperature(value, unit, decimals)

    case AlertRuleName.WEAK_SIGNAL:
      return formatRssi(value, unit, decimals)

    default:
      return '-'
  }
}

export function getAlertValueByRuuviTagCondition(
  alert: PrometheusAlert,
  condition: RuuviTagCondition
) {
  switch (alert.labels.alertname) {
    case AlertRuleName.HIGH_TEMPERATURE:
    case AlertRuleName.LOW_TEMPERATURE:
      return condition.temperatureAvg
    case AlertRuleName.HIGH_HUMIDITY:
    case AlertRuleName.LOW_HUMIDITY:
      return condition.humidityAvg
    case AlertRuleName.HIGH_PRESSURE:
    case AlertRuleName.LOW_PRESSURE:
      return condition.pressureAvg
    case AlertRuleName.DEW_POINT:
      return getAlertValue(alert)
    default:
      return null
  }
}

export function getAlertValueByRuuviTagBattery(alert: PrometheusAlert, battery: RuuviTagBattery) {
  switch (alert.labels.alertname) {
    case AlertRuleName.LOW_BATTERY:
      return battery.voltage
    default:
      return null
  }
}

/**
 * @deprecated
 */
export function getHumanizedAlertValue(alert: PrometheusAlert) {
  const unit = getAlertValueUnit(alert)
  const value = getAlertValue(alert)

  if (value === null) {
    return '-'
  } else if (unit !== undefined) {
    return `${value.toFixed(1)} ${unit}`
  } else {
    return `${value.toFixed(1)}`
  }
}

/**
 * @deprecated
 */
export function getHumanizedAlertThresholdValue(alert: PrometheusAlert) {
  const unit = getAlertValueUnit(alert)
  const thresholdValue = getAlertThresholdValue(alert)

  if (thresholdValue === null) {
    return '-'
  } else if (unit === '%') {
    return `${(thresholdValue * 100).toFixed(0)} ${unit}`
  } else if (unit !== undefined) {
    return `${thresholdValue.toFixed(1)} ${unit}`
  } else {
    return `${thresholdValue.toFixed(1)}`
  }
}

export function formatAlertValueBySensor(
  alert: PrometheusAlert,
  sensor: Sensor,
  unit?: boolean,
  decimals?: number
) {
  if (alert.labels.alertname === AlertRuleName.DEVICE_OFFLINE) {
    // FIXME: We could count minutes/seconds since last seen.
    // return formatDistanceStrict(new Date(sensor.time), new Date())
    return '-'
  }

  const value = getAlertValueBySensor(alert, sensor)

  if (value === null) {
    return '-'
  }

  switch (alert.labels.name) {
    case AlertPhase.C1:
    case AlertPhase.C2:
    case AlertPhase.C3:
      return formatPercentage(value, unit, decimals)
    case AlertPhase.V1:
    case AlertPhase.V2:
    case AlertPhase.V3:
      return formatVoltage(value, unit, decimals)
    case AlertPhase.TEMP:
      return formatTemperature(value, unit, decimals)
    case AlertPhase.RH:
      return formatHumidity(value, unit, decimals)
    case AlertPhase.RSSI:
      return formatRssi(value, unit, decimals)
    default:
      return '-'
  }
}

/**
 * Format the alerting current value.
 */
export function formatAlertingCurrentValueBySensor(
  alert: PrometheusAlert,
  sensor: Sensor,
  unit?: boolean,
  decimals?: number
) {
  const value = getAlertingCurrentValueBySensor(alert, sensor)

  if (value === null) {
    return '-'
  }

  return formatCurrent(value, unit, decimals)
}

export function formatAlertValueByRuuviTagCondition(
  alert: PrometheusAlert,
  condition: RuuviTagCondition,
  unit?: boolean,
  decimals?: number
) {
  const value = getAlertValueByRuuviTagCondition(alert, condition)

  if (value === null) {
    return '-'
  }

  switch (alert.labels.alertname) {
    case AlertRuleName.HIGH_TEMPERATURE:
    case AlertRuleName.LOW_TEMPERATURE:
      return formatTemperature(value, unit, decimals ?? 1)

    case AlertRuleName.HIGH_HUMIDITY:
    case AlertRuleName.LOW_HUMIDITY:
      return formatHumidity(value, unit, decimals ?? 0)

    case AlertRuleName.HIGH_PRESSURE:
    case AlertRuleName.LOW_PRESSURE:
      return formatPressure(value, unit, decimals ?? 0)

    case AlertRuleName.DEW_POINT:
      return formatTemperature(value, unit, decimals ?? 1)

    default:
      return '-'
  }
}

export function formatAlertValueByRuuviTagBattery(
  alert: PrometheusAlert,
  battery: RuuviTagBattery,
  unit?: boolean,
  decimals?: number
) {
  const value = getAlertValueByRuuviTagBattery(alert, battery)

  if (value === null) {
    return '-'
  }

  switch (alert.labels.alertname) {
    case AlertRuleName.LOW_BATTERY:
      return formatBatteryVoltage(value, unit, decimals ?? 0)
    default:
      return '-'
  }
}

export function getAlertTimestamp(alert: PrometheusAlert) {
  if (alert?.startsAt) {
    return new Date(alert.startsAt)
  } else if (alert?.activeAt) {
    return new Date(alert.activeAt)
  } else {
    return null
  }
}

export function sortAlertsByDate(alerts: PrometheusAlert[]) {
  return alerts.sort((a, b) => {
    const aDate = getAlertTimestamp(a)
    const bDate = getAlertTimestamp(b)

    if (aDate && bDate) {
      return bDate.getTime() - aDate.getTime()
    } else if (aDate) {
      return -1
    } else if (bDate) {
      return 1
    } else {
      return 0
    }
  })
}

export function getUniqueAlerts(alerts: PrometheusAlert[]) {
  alerts = sortAlertsByDate([...alerts])

  return alerts.reduce((uniqueAlerts: PrometheusAlert[], alert) => {
    const existingAlert = uniqueAlerts.find((a) => {
      if (alert.labels.alertname === AlertRuleName.DEVICE_OFFLINE) {
        return (
          (a.labels.device === alert.labels.device ||
            a.labels.deviceid === alert.labels.deviceid) &&
          a.labels.alertname === AlertRuleName.DEVICE_OFFLINE
        )
      }

      return (
        (a.labels.device === alert.labels.device || a.labels.deviceid === alert.labels.deviceid) &&
        a.labels.alertname === alert.labels.alertname &&
        a.labels.name === alert.labels.name
      )
    })

    if (!existingAlert) {
      uniqueAlerts.push(alert)
    }

    return uniqueAlerts
  }, [])
}

export function makeAlertRuleDataGridRow(
  group: AlertGroup,
  rule: AlertRule,
  userAlertRule?: UserAlertRule
): AlertRuleDataGridRow {
  return {
    id: `${group.name}.${rule.name}:${rule.sensor ?? '*'}`,
    group: group.name,
    name: rule.name,
    sensor: rule?.sensor,
    severity: rule?.severity,
    enabled: rule.enabled,
    variables: rule.variables,
    userAlertRule
  }
}

export function makeAlertRuleDataGridRows(
  alertRules: AlertGroup[],
  userAlertRules: UserAlertRule[],
  enabled = true
) {
  return alertRules
    .map((group) =>
      group.alerts
        .filter((rule) => rule.enabled === enabled)
        .map((rule) => {
          const userAlertRule = userAlertRules.find(
            (userAlertRule) => userAlertRule.alertRuleName === rule.name
          )
          return makeAlertRuleDataGridRow(group, rule, userAlertRule)
        })
    )
    .flat()
}

export function translateAlertRuleVariable(
  rule: AlertRule | AlertRuleDataGridRow,
  key: keyof AlertVariables
): string {
  const field = getFieldByAlertRuleVariable(rule as AlertRuleDataGridRow, key)
  return translateAlertRuleField(field)
}

export function translateAlertRuleField(field: AlertRuleField): string {
  return i18next.t(`AlertRuleField.${field}`, { ns: 'alert' })
}

export function getFieldByAlertRuleVariable(
  row: AlertRule | AlertRuleDataGridRow,
  key: keyof AlertVariables
): AlertRuleField {
  switch (row.name) {
    case AlertRuleName.HIGH_CURRENT:
    case AlertRuleName.OVERLOADED_CURRENT:
    case AlertRuleName.SHORT_CIRCUIT:
    case AlertRuleName.LOW_CURRENT:
      return 'current'
    case AlertRuleName.HIGH_VOLTAGE:
    case AlertRuleName.LOW_VOLTAGE:
    case AlertRuleName.NO_VOLTAGE:
    case AlertRuleName.LOW_BATTERY:
      return 'voltage'
    case AlertRuleName.HIGH_TEMPERATURE:
    case AlertRuleName.LOW_TEMPERATURE:
      return 'temperature'
    case AlertRuleName.HIGH_HUMIDITY:
    case AlertRuleName.LOW_HUMIDITY:
      return 'humidity'
    case AlertRuleName.DEW_POINT:
      return 'dewPoint'
    case AlertRuleName.HIGH_PRESSURE:
    case AlertRuleName.LOW_PRESSURE:
      return 'pressure'
    case AlertRuleName.CONDENSATION_RISK_INTAKE:
      return 'inDewPoint'
    case AlertRuleName.CONDENSATION_RISK_EXHAUST:
      return 'outDewPoint'
    case AlertRuleName.DEVICE_OFFLINE:
      return 'minutes'
    case AlertRuleName.EXHAUST_AIR_TOO_COLD:
    case AlertRuleName.EXHAUST_AIR_TOO_HOT:
      return 'temperature'
    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
    case AlertRuleName.NO_HEATING:
      switch (key) {
        case 'param1':
          return 'temperature'
        case 'param2':
          return 'setPoint'
        default:
          return 'unknown'
      }
    case AlertRuleName.TARGET_TEMPERATURE_NOT_REACHED:
      switch (key) {
        case 'param1':
          return 'temperature'
        case 'param2':
          return 'minutes'
        default:
          return 'unknown'
      }
    case AlertRuleName.RAPID_TEMPERATURE_DROP:
    case AlertRuleName.RAPID_TEMPERATURE_RISE:
      return 'temperature'
    case AlertRuleName.WEAK_SIGNAL:
    case AlertRuleName.LOW_SIGNAL:
      return 'rssi'
    default:
      return 'unknown'
  }
}

export function deepMergeAlertGroups(
  defaults: AlertGroup[],
  alertGroups: AlertGroup[],
  sensor?: AlertSensor
): AlertGroup[] {
  const deepMergedAlertGroups = [...defaults]

  for (const alertGroup of alertGroups) {
    const index = deepMergedAlertGroups.findIndex((g) => g.name === alertGroup.name)
    if (index === -1) {
      deepMergedAlertGroups.push(alertGroup)
    } else {
      deepMergedAlertGroups[index] = {
        name: alertGroup.name,
        alerts: deepMergeAlertRules(deepMergedAlertGroups[index].alerts, alertGroup.alerts, sensor)
      }
    }
  }

  return deepMergedAlertGroups
}

export function deepMergeAlertRules(
  defaults: AlertRule[],
  alertRules: AlertRule[],
  sensor?: AlertSensor
): AlertRule[] {
  const deepMergedAlertRules = defaults.map((rule) => ({ ...rule, sensor }) as AlertRule)

  for (const alertRule of alertRules) {
    const index = deepMergedAlertRules.findIndex((r) => r.name === alertRule.name)
    if (index === -1) {
      deepMergedAlertRules.push(alertRule)
    } else {
      if (!deepMergedAlertRules[index].enabled) {
        deepMergedAlertRules[index] = { ...alertRule }
      } else {
        deepMergedAlertRules[index] = { ...alertRule, enabled: true, sensor }
      }
    }
  }

  return deepMergedAlertRules
}

export function isProjectAlertTemplate(template: AlertTemplate): boolean {
  return (
    Object.values(template.rules).some((rule) => rule.labels.sensor === undefined) &&
    isProjectAlertGroupName(template.name)
  )
}

export function isProjectAlertGroup(group: AlertGroup): boolean {
  return (
    group.alerts.some((alert) => alert.sensor === undefined) && isProjectAlertGroupName(group.name)
  )
}

function isProjectAlertGroupName(name: AlertGroupName): boolean {
  return (
    name === AlertGroupName.CURRENT ||
    name === AlertGroupName.DEVICE ||
    name === AlertGroupName.HUMIDITY ||
    name === AlertGroupName.SIGNAL ||
    name === AlertGroupName.TEMPERATURE ||
    name === AlertGroupName.VOLTAGE
  )
}

export function isDeviceAlertTemplate(template: AlertTemplate): boolean {
  return (
    Object.values(template.rules).some((rule) => rule.labels.sensor === AlertSensor.DEVICE) &&
    isDeviceAlertGroupName(template.name)
  )
}

export function isDeviceAlertGroup(group: AlertGroup): boolean {
  return (
    group.alerts.some((alert) => alert.sensor === AlertSensor.DEVICE) &&
    isDeviceAlertGroupName(group.name)
  )
}

function isDeviceAlertGroupName(name: AlertGroupName): boolean {
  return (
    name === AlertGroupName.CURRENT ||
    name === AlertGroupName.DEVICE ||
    name === AlertGroupName.HUMIDITY ||
    name === AlertGroupName.SIGNAL ||
    name === AlertGroupName.TEMPERATURE ||
    name === AlertGroupName.VOLTAGE
  )
}

export function isValveAlertTemplate(template: AlertTemplate): boolean {
  return (
    Object.values(template.rules).some((rule) => rule.labels.sensor === AlertSensor.VALVE) &&
    isValveAlertGroupName(template.name)
  )
}

export function isValveAlertGroup(alertGroup: AlertGroup): boolean {
  return (
    alertGroup.alerts.some((alert) => alert.sensor === AlertSensor.VALVE) &&
    isValveAlertGroupName(alertGroup.name)
  )
}

function isValveAlertGroupName(name: AlertGroupName): boolean {
  return (
    name === AlertGroupName.CONDENSATION_RISK ||
    name === AlertGroupName.DEVICE_OFFLINE ||
    name === AlertGroupName.EXHAUST_AIR_TOO_COLD ||
    name === AlertGroupName.EXHAUST_AIR_TOO_HOT ||
    name === AlertGroupName.HEATING_WHILE_VALVE_CLOSED ||
    name === AlertGroupName.LOW_BATTERY ||
    name === AlertGroupName.NO_HEATING ||
    name === AlertGroupName.TARGET_TEMPERATURE_NOT_REACHED ||
    name === AlertGroupName.TEMPERATURE_RAPID_CHANGE ||
    name === AlertGroupName.WEAK_SIGNAL
  )
}

export function isRuuviTagAlertTemplate(template: AlertTemplate): boolean {
  return (
    Object.values(template.rules).some((rule) => rule.labels.sensor === AlertSensor.RUUVITAG) &&
    isRuuviTagAlertGroupName(template.name)
  )
}

export function isRuuviTagAlertGroup(alertGroup: AlertGroup): boolean {
  return (
    alertGroup.alerts.some((alert) => alert.sensor === AlertSensor.RUUVITAG) &&
    isRuuviTagAlertGroupName(alertGroup.name)
  )
}

function isRuuviTagAlertGroupName(name: AlertGroupName): boolean {
  return (
    name === AlertGroupName.BATTERY ||
    name === AlertGroupName.DEW_POINT ||
    name === AlertGroupName.HUMIDITY ||
    name === AlertGroupName.PRESSURE ||
    name === AlertGroupName.TEMPERATURE
  )
}

export function isProjectAlertRule(rule: AlertRule | AlertRuleDataGridRow) {
  return rule.sensor === undefined
}

export function isDeviceAlertRule(rule: AlertRule | AlertRuleDataGridRow) {
  return rule.sensor === AlertSensor.DEVICE
}

export function isValveAlertRule(rule: AlertRule | AlertRuleDataGridRow) {
  return rule.sensor === AlertSensor.VALVE
}

export function isRuuviTagAlertRule(rule: AlertRule | AlertRuleDataGridRow) {
  return rule.sensor === AlertSensor.RUUVITAG
}

export function isSensorAlert(alert: PrometheusAlert) {
  return alert.labels.sensor === AlertSensor.DEVICE
}

export function isValveSensorAlert(alert: PrometheusAlert) {
  return alert.labels.sensor === AlertSensor.VALVE
}

export function isRuuviTagConditionAlert(alert: PrometheusAlert) {
  return (
    alert.labels.sensor === AlertSensor.RUUVITAG &&
    alert.labels.alertname !== AlertRuleName.LOW_BATTERY
  )
}

export function isRuuviTagBatteryAlert(alert: PrometheusAlert) {
  return (
    alert.labels.sensor === AlertSensor.RUUVITAG &&
    alert.labels.alertname === AlertRuleName.LOW_BATTERY
  )
}
